import { useDispatch } from 'react-redux'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  IconButtonProps,
  Tooltip
} from '@mui/material'
import { Edit as EditIcon } from '@mui/icons-material'
import { hideLevel, showLevel } from '../redux/slices/levels'
import { useTranslation } from 'react-i18next'
import { Project, Level } from '../types'
import { useState } from 'react'
import LevelForm from './LevelForm'
import { usePolicy } from '../hooks'
import { ProjectStateType } from '../enums/ProjectStateType'

export interface LevelEditButtonProps extends Omit<IconButtonProps, 'onClick' | 'children'> {
  level: Level
  project?: Project
}

export default function LevelEditButton({ level, project, ...props }: LevelEditButtonProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policies.
   */
  const { canEditProject } = usePolicy()

  /**
   * Indicates if the dialog is open.
   */
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  /**
   * Open the dialog.
   */
  function openDialog(): void {
    dispatch(showLevel(level))
    setIsDialogOpen(true)
  }

  /**
   * Close the dialog.
   */
  function closeDialog(): void {
    setIsDialogOpen(false)
    dispatch(hideLevel())
  }

  return (
    <>
      {project && project.state !== ProjectStateType.ARCHIVED && canEditProject(project) && (
        <Tooltip title={t('edit')}>
          <IconButton size="small" {...props} onClick={() => openDialog()}>
            <EditIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}

      <Dialog open={isDialogOpen} maxWidth="sm" fullWidth onClose={() => closeDialog()}>
        <DialogTitle>{t('editLevel')}</DialogTitle>
        <DialogContent>
          <LevelForm
            value={{ project: project?.id }}
            onCancel={() => closeDialog()}
            afterSubmit={() => closeDialog()}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
